import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import '../styles/register.css';
import parse from 'html-react-parser';
import { TermsConditionsSkeleton } from '../components/Skeletons';

const TermsConditions = () => {
    const { title } = useParams();
    const navigate = useNavigate();
    const [terms, setTerms] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchTerms = async () => {
            try {
                // First try to get from communications endpoint
                let response;
                try {
                    response = await axios.get(`${config.backendUrl}/communications/terms-conditions/${title}/`);
                    setTerms(response.data);
                } catch (commErr) {
                    // Fallback: Try to get from contact-info endpoint
                    const contactResponse = await axios.get(`${config.backendUrl}/contact-info/`);
                    const contactInfo = contactResponse.data;
                    
                    // Map title to corresponding field in contact info
                    if (title === 'conditions-utilisation' && contactInfo.terms_and_conditions) {
                        setTerms({
                            title: 'Conditions d\'Utilisation - Wennze',
                            content: contactInfo.terms_and_conditions
                        });
                    } else if (title === 'politique-confidentialite' && contactInfo.privacy_policy) {
                        setTerms({
                            title: 'Politique de Confidentialité - Wennze',
                            content: contactInfo.privacy_policy
                        });
                    }
                    // If no data from contact info, will use hardcoded fallback
                }
            } catch (err) {
                console.error('Failed to load terms:', err);
                // Don't set error - will use hardcoded fallback
            } finally {
                setLoading(false);
            }
        };

        fetchTerms();
    }, [title]);

    // Function to convert HTML to properly formatted plain text
    const formatHtmlToText = (html) => {
        // Create a temporary div to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Replace block-level elements with proper line breaks
        const blockElements = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6, p, div, li, br');
        blockElements.forEach(element => {
            if (element.tagName === 'BR') {
                element.replaceWith('\n');
            } else if (element.tagName === 'LI') {
                const text = element.textContent;
                element.replaceWith(`• ${text}\n`);
            } else {
                const text = element.textContent;
                if (element.tagName.startsWith('H')) {
                    // Add extra spacing for headers
                    element.replaceWith(`\n\n${text.toUpperCase()}\n${'='.repeat(Math.min(text.length, 60))}\n`);
                } else {
                    element.replaceWith(`${text}\n`);
                }
            }
        });

        // Get the text content and clean it up
        let textContent = tempDiv.textContent || tempDiv.innerText || '';
        
        // Remove excessive whitespace while preserving intentional line breaks
        textContent = textContent
            .replace(/[ \t]+/g, ' ')  // Replace multiple spaces/tabs with single space
            .replace(/\n{3,}/g, '\n\n')  // Replace 3+ line breaks with 2
            .trim();

        return textContent;
    };

    // Function definitions
    const handleGoBack = () => {
        if (window.history.length > 2) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    const handleDownload = () => {
        let content;
        if (terms?.content) {
            content = { title: terms.title || 'Termes et Conditions', content: terms.content };
        } else {
            content = hardcodedContent[title];
        }
        
        if (!content) return;
        
        // Create properly formatted text content for download
        const textContent = formatHtmlToText(content.content);
        const element = document.createElement('a');
        const file = new Blob([textContent], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = `${content.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const handleCopy = async () => {
        let content;
        if (terms?.content) {
            content = { title: terms.title || 'Termes et Conditions', content: terms.content };
        } else {
            content = hardcodedContent[title];
        }
        
        if (!content) return;
        
        // Create properly formatted text version for copying
        const textContent = formatHtmlToText(content.content);
            
        try {
            await navigator.clipboard.writeText(textContent);
            alert('Contenu copié dans le presse-papiers!');
        } catch (err) {
            // Fallback for browsers that don't support clipboard API
            const textArea = document.createElement('textarea');
            textArea.value = textContent;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            alert('Contenu copié dans le presse-papiers!');
        }
    };

    const hardcodedContent = {
        'conditions-utilisation': {
            title: 'Conditions d\'Utilisation - Wennze',
            content: `
                <h1>Conditions d'Utilisation - Wennze</h1>
                <p><strong>Dernière mise à jour :</strong> ${new Date().toLocaleDateString('fr-FR')}</p>
                
                <h2>1. Acceptation des Conditions</h2>
                <p>En accédant et en utilisant la plateforme Wennze (le "Service"), vous acceptez d'être lié par ces Conditions d'Utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser notre service.</p>
                
                <h2>2. Description du Service</h2>
                <p>Wennze est une plateforme de marketplace en ligne qui permet aux utilisateurs de :</p>
                <ul>
                    <li>Publier et vendre des produits et services</li>
                    <li>Acheter des produits auprès d'autres utilisateurs</li>
                    <li>Communiquer avec d'autres utilisateurs</li>
                    <li>Gérer leur profil et leurs transactions</li>
                </ul>
                
                <h2>3. Inscription et Compte Utilisateur</h2>
                <p>Pour utiliser Wennze, vous devez :</p>
                <ul>
                    <li>Être âgé d'au moins 18 ans ou avoir l'autorisation parentale</li>
                    <li>Fournir des informations exactes, complètes et à jour</li>
                    <li>Maintenir la sécurité de votre compte et mot de passe</li>
                    <li>Vérifier votre numéro de téléphone par SMS</li>
                    <li>Nous notifier immédiatement de toute utilisation non autorisée</li>
                </ul>
                
                <h2>4. Droits et Licences Accordés à Wennze</h2>
                <p>En utilisant notre service, vous accordez à Wennze :</p>
                <ul>
                    <li>Le droit d'utiliser, reproduire, et afficher votre contenu sur la plateforme</li>
                    <li>Le droit de traiter vos données selon notre Politique de Confidentialité</li>
                    <li>Le droit de vous envoyer des communications liées au service</li>
                    <li>Le droit de modérer et supprimer le contenu non conforme</li>
                    <li>Le droit de suspendre ou fermer votre compte en cas de violation</li>
                </ul>
                
                <h2>5. Responsabilités de l'Utilisateur</h2>
                <p>Vous vous engagez à :</p>
                <ul>
                    <li>Respecter toutes les lois locales et internationales applicables</li>
                    <li>Ne pas publier de contenu illégal, trompeur ou offensant</li>
                    <li>Respecter les droits de propriété intellectuelle d'autrui</li>
                    <li>Fournir des descriptions précises de vos produits</li>
                    <li>Honorer vos engagements de vente et d'achat</li>
                    <li>Ne pas utiliser la plateforme à des fins frauduleuses</li>
                </ul>
                
                <h2>6. Transactions et Paiements</h2>
                <p>Wennze facilite les transactions entre utilisateurs. Nous ne sommes pas responsables de :</p>
                <ul>
                    <li>La qualité, la sécurité ou la légalité des produits</li>
                    <li>L'exactitude des annonces</li>
                    <li>La capacité des vendeurs à finaliser les ventes</li>
                    <li>Les litiges entre acheteurs et vendeurs</li>
                    <li><strong>La sécurité lors des livraisons et rencontres en personne</strong></li>
                </ul>
                
                <h3><strong>IMPORTANT - RESPONSABILITÉ LORS DES LIVRAISONS :</strong></h3>
                <ul>
                    <li><strong>Wennze n'est qu'une plateforme de mise en relation entre acheteurs et vendeurs</strong></li>
                    <li><strong>Nous ne sommes PAS responsables de la sécurité des utilisateurs lors des livraisons, échanges ou rencontres</strong></li>
                    <li><strong>Il est de VOTRE responsabilité de prendre toutes les précautions nécessaires lors des transactions en personne</strong></li>
                </ul>
                <p><strong>Nous recommandons fortement de :</strong></p>
                <ul>
                    <li>Rencontrer dans des lieux publics et fréquentés</li>
                    <li>Être accompagné lors des échanges</li>
                    <li>Vérifier l'identité de l'autre partie</li>
                    <li>Informer un proche de votre rendez-vous</li>
                    <li>Ne jamais partager vos informations bancaires sensibles</li>
                </ul>
                <p><strong>En cas de problème, contactez immédiatement les autorités locales</strong></p>
                <p style="color: red;"><strong>Wennze décline toute responsabilité en cas d'incident, vol, agression ou problème survenant lors d'une transaction</strong></p>
                
                <p>Les frais de service et commissions sont clairement indiqués avant chaque transaction.</p>
                
                <h2>7. Propriété Intellectuelle</h2>
                <p>Wennze et ses contenus sont protégés par les droits d'auteur et autres droits de propriété intellectuelle. Vous ne pouvez pas :</p>
                <ul>
                    <li>Copier, modifier ou distribuer notre contenu sans autorisation</li>
                    <li>Utiliser nos marques commerciales sans permission</li>
                    <li>Créer des œuvres dérivées de notre plateforme</li>
                </ul>
                
                <h2>8. Limitation de Responsabilité</h2>
                <p>Dans la mesure permise par la loi, Wennze limite sa responsabilité et ne sera pas tenue responsable de :</p>
                <ul>
                    <li>Dommages indirects, consécutifs ou punitifs</li>
                    <li>Perte de profits, données ou opportunités commerciales</li>
                    <li>Interruptions de service ou problèmes techniques</li>
                    <li>Actions ou omissions d'autres utilisateurs</li>
                </ul>
                <p>Notre responsabilité totale ne dépassera pas le montant des frais payés par vous au cours des 12 derniers mois.</p>
                
                <h2>9. Indemnisation</h2>
                <p>Vous acceptez d'indemniser et de dégager Wennze de toute responsabilité concernant :</p>
                <ul>
                    <li>Vos violations de ces conditions</li>
                    <li>Votre utilisation du service</li>
                    <li>Votre contenu publié sur la plateforme</li>
                    <li>Vos interactions avec d'autres utilisateurs</li>
                </ul>
                
                <h2>10. Résiliation</h2>
                <p>Nous pouvons suspendre ou résilier votre compte à tout moment en cas de :</p>
                <ul>
                    <li>Violation de ces conditions</li>
                    <li>Activité frauduleuse ou illégale</li>
                    <li>Inactivité prolongée</li>
                    <li>Demande des autorités compétentes</li>
                </ul>
                
                <h2>11. Droit Applicable et Juridiction</h2>
                <p>Ces conditions sont régies par le droit de la République Démocratique du Congo. Tout litige sera soumis à la juridiction exclusive des tribunaux de Kinshasa.</p>
                
                <h2>12. Modifications</h2>
                <p>Nous nous réservons le droit de modifier ces conditions à tout moment. Les modifications importantes vous seront notifiées par email ou via l'application.</p>
                
                <h2>13. Contact</h2>
                <p>Pour toute question concernant ces conditions :</p>
                <ul>
                    <li>Email : support@wennze.com</li>
                    <li>WhatsApp : +243 851 054 526</li>
                    <li>Adresse : Kinshasa, République Démocratique du Congo</li>
                </ul>
            `
        },
        'politique-confidentialite': {
            title: 'Politique de Confidentialité - Wennze',
            content: `
                <h1>Politique de Confidentialité - Wennze</h1>
                <p><strong>Dernière mise à jour :</strong> ${new Date().toLocaleDateString('fr-FR')}</p>
                
                <h2>1. Introduction</h2>
                <p>Wennze respecte votre vie privée et s'engage à protéger vos données personnelles. Cette politique explique comment nous collectons, utilisons et protégeons vos informations.</p>
                
                <h2>2. Données Collectées</h2>
                <h3>2.1 Informations que vous nous fournissez :</h3>
                <ul>
                    <li>Nom complet et informations de profil</li>
                    <li>Adresse email et numéro de téléphone</li>
                    <li>Photos de profil et de produits</li>
                    <li>Descriptions de produits et services</li>
                    <li>Messages et communications</li>
                    <li>Informations de paiement (stockées de manière sécurisée)</li>
                </ul>
                <h3>2.2 Informations collectées automatiquement :</h3>
                <ul>
                    <li>Adresse IP et données de géolocalisation</li>
                    <li>Type d'appareil et système d'exploitation</li>
                    <li>Données d'utilisation et de navigation</li>
                    <li>Cookies et technologies similaires</li>
                    <li>Horodatage des actions</li>
                </ul>
                <h2>3. Utilisation des Données</h2>
                <p>Nous utilisons vos données pour :</p>
                <ul>
                    <li>Fournir et améliorer nos services</li>
                    <li>Traiter vos transactions</li>
                    <li>Vérifier votre identité (vérification SMS)</li>
                    <li>Prévenir la fraude et les abus</li>
                    <li>Vous envoyer des notifications importantes</li>
                    <li>Personnaliser votre expérience</li>
                    <li>Analyser l'utilisation de la plateforme</li>
                    <li>Respecter nos obligations légales</li>
                    <li><strong>Vous proposer des publicités ciblées basées sur votre localisation et vos intérêts</strong></li>
                    <li><strong>Partager votre numéro de téléphone et votre localisation avec nos partenaires publicitaires pour des campagnes marketing personnalisées</strong></li>
                </ul>
                
                <h3><strong>3.1 COMMUNICATIONS PAR SMS :</strong></h3>
                <p style="color: red;"><strong>EN UTILISANT NOTRE SERVICE, VOUS CONSENTEZ EXPLICITEMENT À RECEVOIR :</strong></p>
                <ul>
                    <li><strong>Des SMS de notification sur vos transactions et activités de compte</strong></li>
                    <li><strong>Des SMS publicitaires et promotionnels de Wennze</strong></li>
                    <li><strong>Des SMS marketing de nos partenaires commerciaux</strong></li>
                    <li><strong>Des mises à jour sur nos services et nouvelles fonctionnalités</strong></li>
                    <li><strong>Des offres spéciales et réductions exclusives</strong></li>
                </ul>
                
                <p><strong>Ces SMS peuvent être envoyés :</strong></p>
                <ul>
                    <li>À tout moment durant les heures d'ouverture (8h-20h)</li>
                    <li>Plusieurs fois par semaine selon les campagnes en cours</li>
                    <li>À votre numéro de téléphone vérifié</li>
                </ul>
                
                <p><strong>Vous pouvez vous désabonner des SMS marketing à tout moment en :</strong></p>
                <ul>
                    <li>Répondant "STOP" à n'importe quel SMS marketing</li>
                    <li>Modifiant vos préférences dans les paramètres de l'application</li>
                    <li>Contactant notre support client</li>
                </ul>
                
                <p><strong>Note :</strong> Même après désabonnement des SMS marketing, vous continuerez à recevoir les SMS transactionnels essentiels (notifications de compte, confirmations de transaction, alertes de sécurité).</p>
                
                <h2>4. Partage des Données</h2>
                <p>Nous ne vendons jamais vos données. Nous pouvons les partager avec :</p>
                <ul>
                    <li><strong>Autres utilisateurs :</strong> Informations de profil public et annonces</li>
                    <li><strong>Prestataires de services :</strong> Paiement, SMS, hébergement (sous contrat strict)</li>
                    <li><strong>Autorités légales :</strong> Si requis par la loi ou pour protéger nos droits</li>
                    <li><strong>Partenaires commerciaux :</strong> Données agrégées et anonymisées uniquement</li>
                </ul>
                
                <h2>5. Sécurité des Données</h2>
                <p>Nous protégeons vos données par :</p>
                <ul>
                    <li>Chiffrement SSL/TLS pour toutes les transmissions</li>
                    <li>Hachage sécurisé des mots de passe</li>
                    <li>Contrôles d'accès stricts</li>
                    <li>Surveillance continue de la sécurité</li>
                    <li>Sauvegardes régulières et sécurisées</li>
                    <li>Audits de sécurité périodiques</li>
                </ul>
                
                <h2>6. Vos Droits</h2>
                <p>Vous avez le droit de :</p>
                <ul>
                    <li><strong>Accès :</strong> Demander une copie de vos données</li>
                    <li><strong>Rectification :</strong> Corriger les informations inexactes</li>
                    <li><strong>Suppression :</strong> Demander la suppression de vos données</li>
                    <li><strong>Portabilité :</strong> Obtenir vos données dans un format lisible</li>
                    <li><strong>Opposition :</strong> Vous opposer à certains traitements</li>
                    <li><strong>Limitation :</strong> Limiter l'utilisation de vos données</li>
                </ul>
                
                <h2>7. Conservation des Données</h2>
                <p>Nous conservons vos données :</p>
                <ul>
                    <li>Tant que votre compte est actif</li>
                    <li>Pendant 3 ans après la fermeture du compte (pour les obligations légales)</li>
                    <li>Plus longtemps si requis par la loi</li>
                    <li>Les données anonymisées peuvent être conservées indéfiniment</li>
                </ul>
                
                <h2>8. Cookies et Technologies Similaires</h2>
                <p>Nous utilisons des cookies pour :</p>
                <ul>
                    <li>Maintenir votre session connectée</li>
                    <li>Mémoriser vos préférences</li>
                    <li>Analyser l'utilisation du site</li>
                    <li>Améliorer la sécurité</li>
                </ul>
                <p>Vous pouvez gérer les cookies dans les paramètres de votre navigateur.</p>
                
                <h2>9. Transferts Internationaux</h2>
                <p>Vos données peuvent être transférées et traitées dans d'autres pays pour :</p>
                <ul>
                    <li>Hébergement et stockage cloud</li>
                    <li>Services de paiement</li>
                    <li>Support technique</li>
                </ul>
                <p>Nous nous assurons que ces transferts respectent les normes de protection appropriées.</p>
                
                <h2>10. Mineurs</h2>
                <p>Nos services ne sont pas destinés aux mineurs de moins de 18 ans. Si nous découvrons qu'un mineur a créé un compte, nous le supprimerons immédiatement.</p>
                
                <h2>11. Modifications de cette Politique</h2>
                <p>Nous pouvons modifier cette politique de confidentialité. Les changements importants vous seront notifiés par email ou via l'application 30 jours avant leur entrée en vigueur.</p>
                
                <h2>12. Contact</h2>
                <p>Pour toute question concernant cette politique ou vos données :</p>
                <ul>
                    <li>Email : privacy@wennze.com</li>
                    <li>WhatsApp : +243 851 054 526</li>
                    <li>Courrier : Délégué à la Protection des Données, Wennze, Kinshasa, RDC</li>
                </ul>
            `
        }
    };

    if (loading) {
        return <TermsConditionsSkeleton />;
    }

    if (error) {
        return (
            <div className="terms-page-container">
                <div className="terms-header">
                    <button onClick={handleGoBack} className="terms-back-button">
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <h1 className="terms-header-title">Termes et Conditions</h1>
                    <div className="terms-header-spacer"></div>
                </div>
                <div className="terms-error-container">
                    <i className="fas fa-exclamation-circle terms-error-icon"></i>
                    <p className="terms-error-text">{error}</p>
                    <button onClick={handleGoBack} className="terms-error-button">
                        <i className="fas fa-arrow-left"></i> Retour
                    </button>
                </div>
            </div>
        );
    }

    // Get content from terms or fallback
    const content = terms?.content ? { title: terms.title, content: terms.content } : hardcodedContent[title];

    if (!content) {
        return (
            <div className="terms-page-container">
                <div className="terms-header">
                    <button onClick={handleGoBack} className="terms-back-button">
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <h1 className="terms-header-title">Termes et Conditions</h1>
                    <div className="terms-header-spacer"></div>
                </div>
                <div className="terms-error-container">
                    <i className="fas fa-file-alt terms-error-icon"></i>
                    <p className="terms-error-text">Le contenu que vous recherchez n'est pas disponible pour le moment.</p>
                    <button onClick={handleGoBack} className="terms-error-button">
                        <i className="fas fa-arrow-left"></i> Retour
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="terms-page-container">
            {/* Fixed Header */}
            <div className="terms-header">
                <button onClick={handleGoBack} className="terms-back-button">
                    <i className="fas fa-arrow-left"></i>
                </button>
                <h1 className="terms-header-title">{content.title || 'Termes et Conditions'}</h1>
                <div className="terms-header-actions">
                    <button onClick={handleDownload} className="terms-action-button" title="Télécharger">
                        <i className="fas fa-download"></i>
                    </button>
                    <button onClick={handleCopy} className="terms-action-button" title="Copier">
                        <i className="fas fa-copy"></i>
                    </button>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="terms-scroll-container">
                <div className="terms-content">
                    {parse(content.content)}
                </div>
            </div>
        </div>
    );
};

export default TermsConditions;